import React, { useState, useEffect } from 'react';
import { contactsAPI, blogsAPI, adminAuthAPI } from '../../lib/supabase';
import './AdminDashboard.css';

const AdminDashboard = ({ onLogout }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [activeSection, setActiveSection] = useState('dashboard'); // 'dashboard', 'blog', 'leads'
  const [activeTab, setActiveTab] = useState('list'); // for blog section
  const [blogs, setBlogs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editingBlog, setEditingBlog] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [viewingLead, setViewingLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [showBlogDeleteModal, setShowBlogDeleteModal] = useState(false);
  const [blogToDelete, setBlogToDelete] = useState(null);
  const [viewingBlog, setViewingBlog] = useState(null);
  const [showBlogViewModal, setShowBlogViewModal] = useState(false);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [editedLead, setEditedLead] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    description: '',
    publishDate: '',
    image: '',
    content: ''
  });

  // Settings page states
  const [adminUser, setAdminUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Sample data
  useEffect(() => {
    // Sample blogs
    const sampleBlogs = [
      {
        id: 1,
        title: 'Solar Energy Benefits for Homes',
        description: 'Discover how solar panels can reduce your electricity bills and help the environment.',
        publishDate: '2025-12-01',
        image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        content: `Solar energy is becoming increasingly popular among homeowners looking to reduce their electricity bills and environmental impact. Here are the key benefits:

**Cost Savings:**
- Reduce electricity bills by 70-90%
- Government subsidies available up to ₹78,000
- ROI within 4-6 years
- 25-year warranty on solar panels

**Environmental Benefits:**
- Zero carbon emissions during operation
- Reduces dependence on fossil fuels
- Each kW solar system saves 1,500 kg CO2 annually
- Contributes to India's renewable energy goals

**Technical Advantages:**
- Low maintenance requirements
- Silent operation
- Modular and scalable systems
- Works in all weather conditions

**Financial Incentives:**
- Net metering facility available
- Tax benefits under Section 80C
- Accelerated depreciation for businesses
- Easy financing options available

Installing solar panels is not just an investment in your home, but also in the future of our planet. With Gujarat's abundant sunshine and supportive government policies, there has never been a better time to go solar.`,
        createdAt: new Date('2025-12-01')
      },
      {
        id: 2,
        title: 'Government Solar Subsidies in Gujarat',
        description: 'Learn about the latest government subsidies available for solar installations.',
        publishDate: '2025-11-28',
        image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
        content: `The Gujarat government has announced comprehensive subsidies for residential and commercial solar installations. Here's everything you need to know:

**Residential Subsidies:**
- Up to ₹78,000 subsidy for systems up to 10kW
- ₹14,588 per kW for systems up to 3kW
- ₹14,588 per kW for first 3kW + ₹7,294 per kW for remaining capacity
- Additional ₹10,000 for systems with battery storage

**Commercial Subsidies:**
- 20% capital subsidy for MSME units
- Accelerated depreciation benefits
- Generation-based incentives available
- Special rates for industrial consumers

**Application Process:**
1. Register on Gujarat Energy Development Agency (GEDA) portal
2. Submit required documents and technical specifications
3. Get technical feasibility approval
4. Install system through empaneled vendors
5. Apply for subsidy after commissioning

**Required Documents:**
- Electricity bill copy
- Property ownership documents
- Aadhaar card and PAN card
- Bank account details
- Technical specifications of solar system

**Timeline:**
- Application processing: 15-30 days
- Installation: 7-15 days after approval
- Subsidy disbursement: 30-60 days after commissioning

The state government aims to achieve 30,000 MW solar capacity by 2030, making these subsidies a crucial step towards renewable energy adoption.`,
        createdAt: new Date('2025-11-28')
      },
      {
        id: 3,
        title: 'Solar Panel Installation Guide',
        description: 'Step-by-step guide to installing solar panels on your roof. Learn about the installation process, safety measures, and what to expect during your solar panel installation.',
        publishDate: '2024-12-05',
        image: 'https://picsum.photos/800/400?random=3',
        content: `Installing solar panels is a significant investment that can provide decades of clean energy and cost savings. This comprehensive guide will walk you through everything you need to know about the solar panel installation process.

**Pre-Installation Assessment**

Before installation begins, our certified technicians conduct a thorough assessment of your property. This includes evaluating your roof's condition, orientation, and structural integrity. We also analyze your energy consumption patterns to design the optimal solar system for your needs.

**The Installation Process**

1. **Permits and Documentation**: We handle all necessary permits and paperwork required by local authorities.

2. **Equipment Delivery**: High-quality solar panels, inverters, and mounting hardware are delivered to your site.

3. **Roof Preparation**: Our team prepares the installation area and marks the locations for mounting points.

4. **Mounting System Installation**: We install the racking system that will securely hold your solar panels.

5. **Panel Installation**: Solar panels are carefully positioned and secured to the mounting system.

6. **Electrical Connections**: All electrical connections are made, including the inverter and monitoring system.

7. **System Testing**: We thoroughly test the entire system to ensure optimal performance.

**Safety First**

Safety is our top priority during installation. Our certified installers use proper safety equipment and follow strict safety protocols. We ensure that all electrical work meets local codes and standards.

**What to Expect**

Most residential installations are completed within 1-3 days, depending on system size and complexity. Our team works efficiently while maintaining the highest quality standards.

**Post-Installation Support**

After installation, we provide comprehensive system monitoring and maintenance support. Our team is always available to address any questions or concerns you may have about your new solar energy system.`,
        createdAt: new Date('2025-12-05')
      },
      {
        id: 4,
        title: 'Government Solar Incentives 2024',
        description: 'Comprehensive overview of government incentives, tax credits, and rebates available for solar installations in 2024. Maximize your savings with these programs.',
        publishDate: '2024-12-03',
        image: 'https://picsum.photos/800/400?random=4',
        content: `The government continues to support renewable energy adoption through various incentives and programs. Here's your complete guide to solar incentives available in 2024.

**Federal Tax Credits**

The federal solar Investment Tax Credit (ITC) allows you to deduct 30% of the cost of installing a solar energy system from your federal taxes. This credit is available through 2032, making now an excellent time to go solar.

**State-Level Incentives**

Many states offer additional incentives on top of federal programs:

- **Cash Rebates**: Direct rebates that reduce the upfront cost of your solar installation
- **Performance-Based Incentives**: Payments based on the actual energy production of your system
- **Property Tax Exemptions**: Many states exempt the added value of solar installations from property taxes

**Net Metering Programs**

Net metering allows you to sell excess energy back to the grid, effectively running your electricity meter backwards. This can significantly reduce or even eliminate your electricity bills.

**Financing Options**

Various financing programs make solar more accessible:

- **Solar Loans**: Low-interest loans specifically for solar installations
- **PACE Financing**: Property Assessed Clean Energy financing tied to your property taxes
- **Leasing Programs**: Options to lease solar systems with little to no upfront cost

**How to Apply**

Our team assists with all incentive applications and paperwork. We ensure you receive every available benefit and maximize your solar investment returns.

**Time-Sensitive Opportunities**

Some incentives have limited funding or expiration dates. Contact us today to learn about current opportunities and secure your solar installation before incentives change.

**ROI Calculator**

With current incentives, most homeowners see a return on investment within 6-8 years, followed by decades of free electricity. Our experts can provide a detailed analysis of your potential savings.`,
        createdAt: new Date('2025-12-03')
      }
    ];

    // Sample leads
    const sampleLeads = [
      {
        id: 1,
        name: 'Rajesh Patel',
        email: 'rajesh@example.com',
        phone: '+91 98765 43210',
        inquiryType: 'Residential Solar',
        subject: 'Need 5KW solar system for home',
        message: `Dear Global Solar Team,

I hope this message finds you well. I am writing to inquire about installing a solar power system for my residential property located in Ahmedabad, Gujarat. After researching various renewable energy options, I have decided that solar power is the best investment for my family's future and the environment.

My house is a 2-story independent villa with approximately 1,200 square feet of usable rooftop space. The roof faces south-east direction and receives excellent sunlight throughout the day from 7 AM to 6 PM. I have consulted with a local architect who confirmed that the roof structure can easily support the weight of solar panels.

Based on my electricity consumption analysis over the past 12 months, my average monthly electricity bill ranges from ₹8,000 to ₹12,000, with peak consumption during summer months due to air conditioning usage. I am looking for a 5KW solar system that can offset at least 80% of my electricity consumption and provide significant savings on my monthly bills.

I would appreciate if you could provide me with:
1. Detailed quotation for 5KW on-grid solar system
2. Information about available government subsidies and tax benefits
3. Expected payback period and ROI calculations
4. Warranty details for panels, inverter, and installation
5. Annual maintenance costs and service packages
6. Timeline for installation and commissioning

I am also interested in knowing about the quality certifications of your solar panels and whether you provide performance monitoring systems. Additionally, please let me know if you offer any financing options or EMI schemes for the installation.

I would prefer to schedule a site visit at your earliest convenience so that your technical team can assess the installation requirements and provide accurate recommendations. I am available for site visits on weekends or after 6 PM on weekdays.

Looking forward to your prompt response and hoping to contribute to a greener future with Global Solar.

Best regards,
Rajesh Patel`,
        status: 'new',
        createdAt: new Date('2025-12-08')
      },
      {
        id: 2,
        name: 'Priya Sharma',
        email: 'priya@example.com',
        phone: '+91 87654 32109',
        inquiryType: 'Commercial Solar',
        subject: 'Solar installation for office building',
        message: `Hello Global Solar Team,

I am Priya Sharma, Operations Manager at TechVision Solutions Pvt. Ltd., and I am reaching out regarding a commercial solar installation project for our office building in Surat.

Our company operates from a modern 3-story commercial building with a total built-up area of 15,000 square feet. The rooftop area available for solar panel installation is approximately 2,000 square feet. The building houses our IT operations, including servers, workstations, air conditioning systems, and other office equipment, resulting in substantial electricity consumption.

Our current monthly electricity expenses range from ₹45,000 to ₹65,000, depending on seasonal variations and operational requirements. We operate 12 hours daily (9 AM to 9 PM) on weekdays and 6 hours on Saturdays. The building has a sanctioned load of 150 KW from Gujarat Electricity Board.

As part of our corporate sustainability initiative and cost optimization strategy, we have decided to invest in renewable energy solutions. Our board of directors has approved a budget of ₹25-30 lakhs for this solar installation project.

We require the following information:
1. Feasibility study and site assessment report
2. Recommended system capacity based on our consumption pattern
3. Detailed project proposal with cost breakdown
4. Expected monthly and annual energy generation
5. Return on investment analysis with payback period
6. Net metering process and documentation support
7. Project timeline from approval to commissioning
8. Comprehensive AMC packages for long-term maintenance

We are particularly interested in high-efficiency solar panels with at least 25-year performance warranty. The system should include advanced monitoring capabilities that allow us to track energy generation and consumption in real-time through web and mobile applications.

Please arrange for a technical site survey at your earliest convenience. Our facility management team will coordinate with your engineers to provide all necessary building drawings, electrical load details, and structural information.

We would also appreciate references from your previous commercial installations in Gujarat, particularly in the IT sector, so we can understand the performance and reliability of your systems.

We are looking to finalize the vendor within the next 30 days and complete the installation before the summer season to maximize energy generation and cost savings.

Thank you for your time and consideration. I look forward to your comprehensive proposal.

Warm regards,
Priya Sharma
Operations Manager
TechVision Solutions Pvt. Ltd.`,
        status: 'contacted',
        createdAt: new Date('2025-12-07')
      },
      {
        id: 3,
        name: 'Amit Kumar',
        email: 'amit@example.com',
        phone: '+91 76543 21098',
        inquiryType: 'Industrial Solar',
        subject: 'Large scale solar project inquiry',
        message: `Dear Global Solar Team,

I am Amit Kumar, Project Director at Gujarat Industrial Manufacturing Ltd., writing to discuss a large-scale solar power project for our manufacturing facility located in Vadodara Industrial Estate.

Our company is a leading manufacturer of automotive components with a production capacity that requires continuous power supply. The facility operates 24/7 across three shifts, with peak power consumption during day shifts when all machinery, including CNC machines, injection molding equipment, and assembly lines are operational.

Current Power Requirements:
- Sanctioned Load: 500 KW
- Average Monthly Consumption: 1,20,000 units
- Monthly Electricity Cost: ₹18-22 lakhs
- Peak Demand: 450 KW (10 AM to 6 PM)
- Off-peak Consumption: 200 KW (night shifts)

Available Infrastructure:
- Total rooftop area: 8,000 square feet
- Additional ground space: 15,000 square feet
- Roof structure: Pre-engineered building capable of additional load
- Electrical infrastructure: 11 KV substation with adequate capacity

We are planning to install a 1 MW solar power system (combination of rooftop and ground-mounted) to reduce our dependency on grid electricity and achieve significant cost savings. This project is part of our green manufacturing initiative and will help us achieve carbon neutrality goals by 2030.

Project Requirements:
1. Detailed feasibility study including shadow analysis
2. Hybrid installation design (rooftop + ground mounted)
3. High-efficiency monocrystalline panels with tier-1 manufacturer warranty
4. String inverters with SCADA monitoring system
5. Complete electrical balance of system including cables, structures, and protection equipment
6. Grid synchronization and net metering setup
7. Comprehensive insurance coverage during and after installation
8. 25-year comprehensive O&M contract with performance guarantees

Financial Considerations:
- Project budget: ₹4-5 crores
- Preferred financing: Mix of equity and debt financing
- Expected payback period: Maximum 6-7 years
- Minimum performance guarantee: 85% for first 10 years, 80% thereafter

Compliance and Certifications:
- All equipment should have IEC certifications
- Installation should comply with CEA regulations
- Environmental clearances and approvals support required
- Integration with existing fire safety and security systems

Timeline Expectations:
- Technical proposal submission: Within 15 days
- Site survey and detailed engineering: 30 days
- Financial closure and approvals: 45 days
- Installation and commissioning: 90 days from work order

We would appreciate if you could arrange a detailed presentation for our technical team and management. Please include case studies of similar industrial installations, performance data, and client references.

Our technical team is available for site visits and discussions. We are looking for a long-term partnership with a reliable solar EPC company that can support our expansion plans across multiple locations in Gujarat and Maharashtra.

Please confirm your interest and availability for an initial meeting. We are eager to move forward with this project and contribute to India's renewable energy goals.

Best regards,
Amit Kumar
Project Director
Gujarat Industrial Manufacturing Ltd.`,
        status: 'converted',
        createdAt: new Date('2025-12-06')
      },
      {
        id: 4,
        name: 'Neha Gupta',
        email: 'neha.gupta@gmail.com',
        phone: '+91 95432 10987',
        inquiryType: 'Residential Solar',
        subject: 'Solar panels for apartment complex',
        message: `Dear Global Solar Team,

I am Neha Gupta, Secretary of Sunrise Apartments Society in Rajkot. Our society consists of 24 flats in a 4-story building, and we are exploring the possibility of installing a solar power system for common area electricity and individual flat connections.

Our monthly common area electricity bill is around ₹15,000, which includes lift operations, water pumps, security lighting, and common area lighting. Additionally, individual flat owners are interested in reducing their personal electricity bills through solar power.

We have approximately 3,000 square feet of rooftop space available. The building structure was constructed in 2018 and can support additional load. We are looking for a comprehensive solution that can benefit all residents.

Could you please provide information about:
1. Group housing solar solutions
2. Individual metering options for each flat
3. Cost-sharing mechanisms among residents
4. Government subsidies for residential societies
5. Maintenance and service agreements

We would like to schedule a society meeting with your technical team to discuss this project in detail.

Best regards,
Neha Gupta`,
        status: 'new',
        createdAt: new Date('2025-12-05')
      },
      {
        id: 5,
        name: 'Dr. Vikram Singh',
        email: 'dr.vikram@healthcareclinic.com',
        phone: '+91 94567 89012',
        inquiryType: 'Commercial Solar',
        subject: 'Solar system for medical clinic',
        message: `Hello Global Solar,

I am Dr. Vikram Singh, owner of HealthCare Plus Clinic in Gandhinagar. Our clinic operates 12 hours daily and requires uninterrupted power supply for medical equipment, air conditioning, and lighting.

Our monthly electricity bill ranges from ₹25,000 to ₹35,000. We have a 1,500 sq ft rooftop area available for solar installation. Given the critical nature of our operations, we need a reliable solar system with battery backup.

We are interested in:
1. On-grid solar system with battery backup
2. Automatic changeover systems
3. UPS integration for critical equipment
4. Remote monitoring capabilities
5. 24/7 technical support

Please provide a detailed proposal including cost analysis and installation timeline.

Regards,
Dr. Vikram Singh`,
        status: 'contacted',
        createdAt: new Date('2025-12-04')
      },
      {
        id: 6,
        name: 'Ravi Mehta',
        email: 'ravi.mehta@farmtech.in',
        phone: '+91 93456 78901',
        inquiryType: 'Agricultural Solar',
        subject: 'Solar water pumping system for farm',
        message: `Dear Team,

I am Ravi Mehta, a progressive farmer from Anand district. I own 25 acres of agricultural land and currently use diesel pumps for irrigation, which is becoming very expensive.

I am interested in installing solar water pumping systems to reduce operational costs and ensure reliable water supply for my crops. My current diesel expenses are around ₹8,000 per month.

Requirements:
1. Solar water pump for 25 acres
2. Drip irrigation compatibility
3. Remote monitoring system
4. Government subsidy assistance
5. Training for operation and maintenance

Please visit my farm for site assessment and provide a comprehensive proposal.

Best regards,
Ravi Mehta`,
        status: 'new',
        createdAt: new Date('2025-12-03')
      },
      {
        id: 7,
        name: 'Kavita Joshi',
        email: 'kavita@educationhub.org',
        phone: '+91 92345 67890',
        inquiryType: 'Institutional Solar',
        subject: 'Solar installation for school campus',
        message: `Hello Global Solar Team,

I am Kavita Joshi, Principal of Bright Future School in Bhavnagar. Our school campus has 20 classrooms, computer lab, library, and administrative offices. We want to install solar panels to reduce electricity costs and teach students about renewable energy.

Our monthly electricity bill is ₹40,000. We have ample rooftop space across multiple buildings totaling 4,000 sq ft. The project should serve as an educational tool for our students.

We need:
1. Educational solar system with display panels
2. Student-friendly monitoring interface
3. Safety features for school environment
4. Comprehensive warranty and support
5. Training programs for teachers and students

Please arrange a presentation for our management committee.

Warm regards,
Kavita Joshi`,
        status: 'contacted',
        createdAt: new Date('2025-12-02')
      },
      {
        id: 8,
        name: 'Suresh Patel',
        email: 'suresh@retailchain.com',
        phone: '+91 91234 56789',
        inquiryType: 'Commercial Solar',
        subject: 'Solar for retail store chain',
        message: `Dear Global Solar,

I am Suresh Patel, owner of a retail chain with 5 stores across Gujarat. Each store has high electricity consumption due to lighting, air conditioning, and refrigeration units.

Combined monthly electricity bill for all stores is ₹1,20,000. We want to install solar systems at all locations to reduce operational costs.

Store details:
- Ahmedabad: 2,000 sq ft rooftop
- Surat: 1,800 sq ft rooftop
- Vadodara: 2,200 sq ft rooftop
- Rajkot: 1,500 sq ft rooftop
- Gandhinagar: 1,700 sq ft rooftop

We need a comprehensive proposal for all locations with centralized monitoring and maintenance.

Best regards,
Suresh Patel`,
        status: 'converted',
        createdAt: new Date('2025-12-01')
      }
    ];
    
    // Initialize with sample data first, then fetch from Supabase
    setBlogs(sampleBlogs);
    setLeads(sampleLeads);
    localStorage.setItem('adminLeads', JSON.stringify(sampleLeads));

    // Fetch real data from Supabase
    fetchBlogsFromSupabase();
    fetchLeadsFromSupabase();
  }, []);

  // Function to fetch blogs from Supabase
  const fetchBlogsFromSupabase = async () => {
    try {
      const blogs = await blogsAPI.getAll();
      
      // Transform Supabase data to match existing blog structure
      const transformedBlogs = blogs.map(blog => ({
        id: blog.id,
        title: blog.title,
        description: blog.description,
        publishDate: blog.publish_date,
        image: blog.image,
        content: blog.content,
        createdAt: new Date(blog.created_at),
        updatedAt: blog.updated_at ? new Date(blog.updated_at) : null
      }));
      
      setBlogs(transformedBlogs);
      console.log('Blogs fetched from Supabase:', transformedBlogs.length);
    } catch (error) {
      console.error('Error fetching blogs from Supabase:', error);
      // Keep sample blogs if Supabase fetch fails
    }
  };

  // Function to fetch leads from Supabase
  const fetchLeadsFromSupabase = async () => {
    try {
      const contacts = await contactsAPI.getAll();
      
      // Transform Supabase data to match existing lead structure
      const transformedLeads = contacts.map(contact => ({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        phone: contact.phone || 'N/A',
        inquiryType: contact.inquiry_type,
        subject: contact.subject || 'General Inquiry',
        message: contact.message,
        status: contact.status,
        createdAt: new Date(contact.created_at)
      }));

      // Combine with sample data or replace completely
      setLeads(transformedLeads.length > 0 ? transformedLeads : sampleLeads);
      
      console.log('Fetched leads from Supabase:', transformedLeads);
    } catch (error) {
      console.error('Error fetching leads from Supabase:', error);
      // Fallback to sample data if Supabase fails
      setLeads(sampleLeads);
    }
  };

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    onLogout();
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Blog functions
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setBlogForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBlogForm(prev => ({
          ...prev,
          image: e.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Mobile sidebar functions
  const toggleMobileSidebar = () => {
    const newState = !mobileSidebarOpen;
    setMobileSidebarOpen(newState);
    
    // Add/remove body class for CSS targeting
    if (newState) {
      document.body.classList.add('mobile-sidebar-open');
    } else {
      document.body.classList.remove('mobile-sidebar-open');
    }
  };

  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
    document.body.classList.remove('mobile-sidebar-open');
  };

  // Close mobile sidebar when clicking on navigation items
  const handleNavClick = (section) => {
    setActiveSection(section);
    setMobileSidebarOpen(false);
    document.body.classList.remove('mobile-sidebar-open');
  };

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileSidebarOpen && !event.target.closest('.admin-sidebar') && !event.target.closest('.mobile-sidebar-toggle')) {
        setMobileSidebarOpen(false);
        document.body.classList.remove('mobile-sidebar-open');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileSidebarOpen]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (mobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileSidebarOpen]);

  // Load admin user data on component mount
  useEffect(() => {
    const loadAdminUser = () => {
      const storedUser = localStorage.getItem('adminUser');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setAdminUser(userData);
        } catch (error) {
          console.error('Error parsing admin user data:', error);
        }
      }
    };

    loadAdminUser();
  }, []);

  // Password change handlers
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (passwordError) setPasswordError('');
    if (passwordSuccess) setPasswordSuccess('');
  };

  // Password visibility toggle functions
  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    try {
      // First verify current password
      const authResult = await adminAuthAPI.authenticate(adminUser.username, passwordForm.currentPassword);
      
      if (!authResult.success) {
        setPasswordError('Current password is incorrect');
        return;
      }

      // Update password in database
      await adminAuthAPI.updatePassword(adminUser.id, passwordForm.newPassword);
      
      setPasswordSuccess('Password updated successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);

    } catch (error) {
      setPasswordError('Failed to update password. Please try again.');
      console.error('Password update error:', error);
    }
  };

  const openPasswordModal = () => {
    setShowPasswordModal(true);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!blogForm.title || !blogForm.description || !blogForm.publishDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const blogData = {
        title: blogForm.title,
        description: blogForm.description,
        publish_date: blogForm.publishDate,
        image: blogForm.image,
        content: blogForm.content
      };

      let savedBlog;
      if (editingBlog) {
        // Update existing blog
        savedBlog = await blogsAPI.update(editingBlog.id, blogData);
        const updatedBlogs = blogs.map(blog =>
          blog.id === editingBlog.id ? {
            id: savedBlog.id,
            title: savedBlog.title,
            description: savedBlog.description,
            publishDate: savedBlog.publish_date,
            image: savedBlog.image,
            content: savedBlog.content,
            createdAt: new Date(savedBlog.created_at),
            updatedAt: new Date(savedBlog.updated_at)
          } : blog
        );
        setBlogs(updatedBlogs);
      } else {
        // Create new blog
        savedBlog = await blogsAPI.create(blogData);
        const newBlog = {
          id: savedBlog.id,
          title: savedBlog.title,
          description: savedBlog.description,
          publishDate: savedBlog.publish_date,
          image: savedBlog.image,
          content: savedBlog.content,
          createdAt: new Date(savedBlog.created_at),
          updatedAt: savedBlog.updated_at ? new Date(savedBlog.updated_at) : null
        };
        setBlogs([newBlog, ...blogs]);
      }

      setBlogForm({
        title: '',
        description: '',
        publishDate: '',
        image: '',
        content: ''
      });
      setEditingBlog(null);
      setActiveTab('list');
      
      console.log('Blog saved successfully:', savedBlog);
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog. Please try again.');
    }
  };

  const handleEdit = (blog) => {
    setBlogForm({
      title: blog.title,
      description: blog.description,
      publishDate: blog.publishDate,
      image: blog.image,
      content: blog.content
    });
    setEditingBlog(blog);
    setActiveTab('add');
  };

  // Blog delete functions - show custom modal
  const handleDelete = (blogId) => {
    setBlogToDelete(blogId);
    setShowBlogDeleteModal(true);
  };

  // Confirm blog delete function
  const confirmDeleteBlog = async () => {
    if (blogToDelete) {
      try {
        // Delete from Supabase
        await blogsAPI.delete(blogToDelete);
        
        // Update local state
        const updatedBlogs = blogs.filter(blog => blog.id !== blogToDelete);
        setBlogs(updatedBlogs);
        
        console.log(`Blog ${blogToDelete} deleted successfully`);
      } catch (error) {
        console.error('Error deleting blog:', error);
        alert('Error deleting blog. Please try again.');
        // Still update local state as fallback
        const updatedBlogs = blogs.filter(blog => blog.id !== blogToDelete);
        setBlogs(updatedBlogs);
      }
    }
    setShowBlogDeleteModal(false);
    setBlogToDelete(null);
  };

  // Cancel blog delete function
  const cancelDeleteBlog = () => {
    setShowBlogDeleteModal(false);
    setBlogToDelete(null);
  };

  const handleNewPost = () => {
    setBlogForm({
      title: '',
      description: '',
      publishDate: '',
      image: '',
      content: ''
    });
    setEditingBlog(null);
    setActiveTab('add');
  };

  // Lead functions
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      // Update in Supabase
      await contactsAPI.updateStatus(leadId, newStatus);
      
      // Update local state
      const updatedLeads = leads.map(lead =>
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      );
      setLeads(updatedLeads);
      localStorage.setItem('adminLeads', JSON.stringify(updatedLeads));
      
      console.log(`Lead ${leadId} status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating lead status:', error);
      // Still update local state as fallback
      const updatedLeads = leads.map(lead =>
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      );
      setLeads(updatedLeads);
      localStorage.setItem('adminLeads', JSON.stringify(updatedLeads));
    }
  };

  // Delete lead function - show custom modal
  const handleDeleteLead = (leadId) => {
    setLeadToDelete(leadId);
    setShowDeleteModal(true);
  };

  // Confirm delete function
  const confirmDeleteLead = async () => {
    if (leadToDelete) {
      try {
        // Delete from Supabase
        await contactsAPI.delete(leadToDelete);
        
        // Update local state
        const updatedLeads = leads.filter(lead => lead.id !== leadToDelete);
        setLeads(updatedLeads);
        localStorage.setItem('adminLeads', JSON.stringify(updatedLeads));
        
        console.log(`Lead ${leadToDelete} deleted successfully`);
      } catch (error) {
        console.error('Error deleting lead:', error);
        // Still update local state as fallback
        const updatedLeads = leads.filter(lead => lead.id !== leadToDelete);
        setLeads(updatedLeads);
        localStorage.setItem('adminLeads', JSON.stringify(updatedLeads));
      }
    }
    setShowDeleteModal(false);
    setLeadToDelete(null);
  };

  // Cancel delete function
  const cancelDeleteLead = () => {
    setShowDeleteModal(false);
    setLeadToDelete(null);
  };

  // Edit lead functions
  const handleEditLead = () => {
    setIsEditingLead(true);
    setEditedLead({
      ...viewingLead
    });
  };

  const handleCancelEdit = () => {
    setIsEditingLead(false);
    setEditedLead(null);
  };

  const handleEditChange = (field, value) => {
    setEditedLead(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateLead = async () => {
    try {
      // Update in Supabase
      await contactsAPI.updateStatus(editedLead.id, editedLead.status);
      
      // Update local state
      const updatedLeads = leads.map(lead =>
        lead.id === editedLead.id ? editedLead : lead
      );
      setLeads(updatedLeads);
      localStorage.setItem('adminLeads', JSON.stringify(updatedLeads));
      
      // Update viewing lead
      setViewingLead(editedLead);
      setIsEditingLead(false);
      setEditedLead(null);
      
      console.log(`Lead ${editedLead.id} updated successfully`);
    } catch (error) {
      console.error('Error updating lead:', error);
      // Still update local state as fallback
      const updatedLeads = leads.map(lead =>
        lead.id === editedLead.id ? editedLead : lead
      );
      setLeads(updatedLeads);
      localStorage.setItem('adminLeads', JSON.stringify(updatedLeads));
      setViewingLead(editedLead);
      setIsEditingLead(false);
      setEditedLead(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#f59e0b';
      case 'contacted': return '#3b82f6';
      case 'converted': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new': return 'New';
      case 'contacted': return 'Contacted';
      case 'converted': return 'Converted';
      default: return 'Unknown';
    }
  };

  // Lead view functions
  const handleViewLead = (lead) => {
    setViewingLead(lead);
    setShowLeadModal(true);
  };

  const closeLeadModal = () => {
    setShowLeadModal(false);
    setViewingLead(null);
    setIsEditingLead(false);
    setEditedLead(null);
  };

  // Direct edit lead function - opens modal in edit mode
  const handleDirectEditLead = (lead) => {
    setViewingLead(lead);
    setEditedLead({ ...lead });
    setIsEditingLead(true);
    setShowLeadModal(true);
  };

  // Blog view functions
  const handleViewBlog = (blog) => {
    setViewingBlog(blog);
    setShowBlogViewModal(true);
  };

  const closeBlogViewModal = () => {
    setShowBlogViewModal(false);
    setViewingBlog(null);
  };

  // Dashboard stats
  const dashboardStats = {
    totalBlogs: blogs.length,
    totalLeads: leads.length,
    newLeads: leads.filter(lead => lead.status === 'new').length,
    convertedLeads: leads.filter(lead => lead.status === 'converted').length
  };

  return (
    <div className="admin-dashboard">
      {/* Mobile Sidebar Toggle */}
      <button
        className="mobile-sidebar-toggle"
        onClick={toggleMobileSidebar}
        aria-label="Toggle Sidebar"
      >
        <i className="fas fa-bars"></i>
      </button>

      {/* Mobile Sidebar Overlay */}
      <div
        className={`mobile-sidebar-overlay ${mobileSidebarOpen ? 'active' : ''}`}
        onClick={closeMobileSidebar}
      ></div>

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/images/global-solar-logo-new.png" alt="Global Solar" />
            {!sidebarCollapsed && (
              <div className="logo-text">
                <h3>Global Solar</h3>
                <p>Admin Panel</p>
              </div>
            )}
          </div>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <i className={`fas fa-${sidebarCollapsed ? 'chevron-right' : 'chevron-left'}`}></i>
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul className="nav-menu">
            <li className={`nav-item ${activeSection === 'dashboard' ? 'active' : ''}`}>
              <button
                className="nav-link"
                onClick={() => handleNavClick('dashboard')}
              >
                <i className="fas fa-tachometer-alt"></i>
                {!sidebarCollapsed && <span>Dashboard</span>}
              </button>
            </li>
            <li className={`nav-item ${activeSection === 'blog' ? 'active' : ''}`}>
              <button
                className="nav-link"
                onClick={() => handleNavClick('blog')}
              >
                <i className="fas fa-blog"></i>
                {!sidebarCollapsed && <span>Blog</span>}
              </button>
            </li>
            <li className={`nav-item ${activeSection === 'leads' ? 'active' : ''}`}>
              <button
                className="nav-link"
                onClick={() => handleNavClick('leads')}
              >
                <i className="fas fa-users"></i>
                {!sidebarCollapsed && <span>Leads</span>}
              </button>
            </li>
            <li className={`nav-item ${activeSection === 'settings' ? 'active' : ''}`}>
              <button
                className="nav-link"
                onClick={() => handleNavClick('settings')}
              >
                <i className="fas fa-cog"></i>
                {!sidebarCollapsed && <span>Settings</span>}
              </button>
            </li>
          </ul>
        </nav>

        <div className="sidebar-footer">
          <div className="admin-profile">
            <div className="admin-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            {!sidebarCollapsed && (
              <div className="admin-info">
                <span className="admin-name">Administrator</span>
                <button onClick={handleLogout} className="logout-btn">
                  <i className="fas fa-sign-out-alt"></i>
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {/* Top Header */}
        <header className="main-header">
          <div className="header-left">
            <h1>
              {activeSection === 'dashboard' && 'Dashboard'}
              {activeSection === 'blog' && 'Blog Management'}
              {activeSection === 'leads' && 'Lead Management'}
              {activeSection === 'settings' && 'Settings'}
            </h1>
          </div>
          <div className="header-right">
            <div className="datetime-display">
              <div className="time">{formatTime(currentTime)}</div>
              <div className="date">{formatDate(currentTime)}</div>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        {activeSection === 'dashboard' && (
          <div className="dashboard-content">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-blog"></i>
                </div>
                <div className="stat-info">
                  <h3>{dashboardStats.totalBlogs}</h3>
                  <p>Total Blogs</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-users"></i>
                </div>
                <div className="stat-info">
                  <h3>{dashboardStats.totalLeads}</h3>
                  <p>Total Leads</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-user-plus"></i>
                </div>
                <div className="stat-info">
                  <h3>{dashboardStats.newLeads}</h3>
                  <p>New Leads</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="stat-info">
                  <h3>{dashboardStats.convertedLeads}</h3>
                  <p>Converted</p>
                </div>
              </div>
            </div>

            <div className="dashboard-widgets">
              <div className="widget">
                <h3>Recent Blogs</h3>
                <div className="recent-items">
                  {blogs.slice(0, 3).map(blog => (
                    <div key={blog.id} className="recent-item">
                      <h4>{blog.title}</h4>
                      <p>{new Date(blog.publishDate).toLocaleDateString('en-IN')}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="widget">
                <h3>Recent Leads</h3>
                <div className="recent-items">
                  {leads.slice(0, 3).map(lead => (
                    <div key={lead.id} className="recent-item">
                      <h4>{lead.name}</h4>
                      <p>{lead.location} - {getStatusLabel(lead.status)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Blog Content */}
        {activeSection === 'blog' && (
          <div className="blog-content">
            {/* Blog Tabs */}
            <div className="content-tabs">
              <button 
                className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
                onClick={() => setActiveTab('list')}
              >
                <i className="fas fa-list"></i>
                All Posts
              </button>
              <button 
                className={`tab-btn ${activeTab === 'add' ? 'active' : ''}`}
                onClick={handleNewPost}
              >
                <i className="fas fa-plus"></i>
                {editingBlog ? 'Edit Post' : 'New Post'}
              </button>
            </div>

            {/* Blog List */}
            {activeTab === 'list' && (
              <div className="blog-list">
                <div className="list-header">
                  <h3>All Blog Posts ({blogs.length})</h3>
                </div>
                
                {blogs.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-blog"></i>
                    <h4>No blog posts yet</h4>
                    <p>Create your first blog post to get started</p>
                    <button className="create-first-btn" onClick={handleNewPost}>
                      Create First Post
                    </button>
                  </div>
                ) : (
                  <div className="blog-grid">
                    {blogs.map(blog => (
                      <div key={blog.id} className="blog-card">
                        {blog.image && (
                          <div className="blog-image">
                            <img src={blog.image} alt={blog.title} />
                          </div>
                        )}
                        <div className="blog-content">
                          <h4>{blog.title}</h4>
                          <div className="blog-meta">
                            <span className="publish-date">
                              <i className="fas fa-calendar"></i>
                              {new Date(blog.publishDate).toLocaleDateString('en-IN')}
                            </span>
                          </div>
                          <div className="blog-actions">
                            <button
                              className="view-btn"
                              onClick={() => handleViewBlog(blog)}
                            >
                              <i className="fas fa-eye"></i>
                              View
                            </button>
                            <button
                              className="edit-btn"
                              onClick={() => handleEdit(blog)}
                            >
                              <i className="fas fa-edit"></i>
                              Edit
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDelete(blog.id)}
                            >
                              <i className="fas fa-trash"></i>
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Blog Form */}
            {activeTab === 'add' && (
              <div className="blog-form-section">
                <div className="form-header">
                  <h3>{editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}</h3>
                  <button 
                    className="back-btn"
                    onClick={() => setActiveTab('list')}
                  >
                    <i className="fas fa-arrow-left"></i>
                    Back to List
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="blog-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="title">Title *</label>
                      <input
                        type="text"
                        id="title"
                        name="title"
                        value={blogForm.title}
                        onChange={handleFormChange}
                        placeholder="Enter blog title"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="description">Description *</label>
                      <textarea
                        id="description"
                        name="description"
                        value={blogForm.description}
                        onChange={handleFormChange}
                        placeholder="Enter blog description"
                        rows="3"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="publishDate">Publish Date *</label>
                      <input
                        type="date"
                        id="publishDate"
                        name="publishDate"
                        value={blogForm.publishDate}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="image">Featured Image</label>
                      <input
                        type="file"
                        id="image"
                        accept="image/*"
                        onChange={handleImageChange}
                      />
                      {blogForm.image && (
                        <div className="image-preview">
                          <img src={blogForm.image} alt="Preview" />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="content">Content</label>
                      <textarea
                        id="content"
                        name="content"
                        value={blogForm.content}
                        onChange={handleFormChange}
                        placeholder="Enter blog content"
                        rows="8"
                      />
                    </div>
                  </div>

                  <div className="form-actions">
                    <button type="button" className="cancel-btn" onClick={() => setActiveTab('list')}>
                      Cancel
                    </button>
                    <button type="submit" className="submit-btn">
                      <i className="fas fa-save"></i>
                      {editingBlog ? 'Update Post' : 'Publish Post'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Leads Content */}
        {activeSection === 'leads' && (
          <div className="leads-content">
            <div className="leads-header">
              <h3>All Leads ({leads.length})</h3>
              <div className="leads-stats">
                <span className="stat-badge new">New: {dashboardStats.newLeads}</span>
                <span className="stat-badge contacted">Contacted: {leads.filter(l => l.status === 'contacted').length}</span>
                <span className="stat-badge converted">Converted: {dashboardStats.convertedLeads}</span>
              </div>
            </div>

            <div className="leads-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Inquiry Type</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id}>
                      <td>{lead.name}</td>
                      <td>{lead.email}</td>
                      <td>{lead.phone}</td>
                      <td>{lead.inquiryType}</td>
                      <td>
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                          className="status-select"
                          style={{ color: getStatusColor(lead.status) }}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                        </select>
                      </td>
                      <td>{new Date(lead.createdAt).toLocaleDateString('en-IN')}</td>
                      <td>
                        <div className="lead-actions">
                          <button
                            className="action-btn view"
                            onClick={() => handleViewLead(lead)}
                          >
                            <i className="fas fa-eye"></i>
                          </button>
                          <button
                            className="action-btn edit"
                            onClick={() => handleDirectEditLead(lead)}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteLead(lead.id)}
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards for Leads */}
            <div className="leads-mobile-cards">
              {leads.map(lead => (
                <div key={lead.id} className="lead-mobile-card">
                  <h4>{lead.name}</h4>
                  <div className="lead-mobile-info">
                    <span><strong>Email:</strong> {lead.email}</span>
                    <span><strong>Phone:</strong> {lead.phone}</span>
                    <span><strong>Inquiry:</strong> {lead.inquiryType}</span>
                    <span><strong>Date:</strong> {new Date(lead.createdAt).toLocaleDateString('en-IN')}</span>
                    <div className="mobile-status-row">
                      <strong>Status:</strong>
                      <select
                        value={lead.status}
                        onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                        className="status-select mobile"
                        style={{ color: getStatusColor(lead.status) }}
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="converted">Converted</option>
                      </select>
                    </div>
                  </div>
                  <div className="lead-mobile-actions">
                    <button
                      className="action-btn view mobile"
                      onClick={() => handleViewLead(lead)}
                    >
                      <i className="fas fa-eye"></i>
                      View
                    </button>
                    <button
                      className="action-btn edit mobile"
                      onClick={() => handleDirectEditLead(lead)}
                    >
                      <i className="fas fa-edit"></i>
                      Edit
                    </button>
                    <button
                      className="action-btn delete mobile"
                      onClick={() => handleDeleteLead(lead.id)}
                    >
                      <i className="fas fa-trash"></i>
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lead Details Modal */}
        {showLeadModal && viewingLead && (
          <div className="modal-overlay" onClick={closeLeadModal}>
            <div className="lead-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Lead Details</h3>
                <button className="close-btn" onClick={closeLeadModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-content">
                <div className="lead-details">
                  {!isEditingLead ? (
                    // View Mode
                    <>
                      {/* Row 1: Name + Phone */}
                      <div className="detail-row-pair">
                        <div className="detail-card">
                          <label>Name:</label>
                          <span>{viewingLead.name}</span>
                        </div>
                        <div className="detail-card">
                          <label>Phone:</label>
                          <span>{viewingLead.phone}</span>
                        </div>
                      </div>
                      
                      {/* Row 2: Email + Inquiry Type */}
                      <div className="detail-row-pair">
                        <div className="detail-card">
                          <label>Email:</label>
                          <span>{viewingLead.email}</span>
                        </div>
                        <div className="detail-card">
                          <label>Inquiry Type:</label>
                          <span>{viewingLead.inquiryType}</span>
                        </div>
                      </div>
                      
                      {/* Row 3: Status + Date */}
                      <div className="detail-row-pair">
                        <div className="detail-card">
                          <label>Status:</label>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: getStatusColor(viewingLead.status),
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              display: 'inline-block',
                              width: 'fit-content',
                              maxWidth: '80px',
                              textAlign: 'center'
                            }}
                          >
                            {getStatusLabel(viewingLead.status)}
                          </span>
                        </div>
                        <div className="detail-card">
                          <label>Date:</label>
                          <span>{new Date(viewingLead.createdAt).toLocaleDateString('en-IN')}</span>
                        </div>
                      </div>
                      
                      {/* Row 4: Subject (single card) */}
                      <div className="detail-row-single">
                        <div className="detail-card full-width">
                          <label>Subject:</label>
                          <span>{viewingLead.subject}</span>
                        </div>
                      </div>
                      
                      {/* Row 5: Message (single card) */}
                      <div className="detail-row-single">
                        <div className="detail-card full-width message-card">
                          <label>Message:</label>
                          <div className="message-content">
                            {viewingLead.message}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    // Edit Mode
                    <>
                      {/* Row 1: Name + Phone */}
                      <div className="detail-row-pair">
                        <div className="detail-card">
                          <label>Name:</label>
                          <input
                            type="text"
                            value={editedLead.name}
                            onChange={(e) => handleEditChange('name', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                        <div className="detail-card">
                          <label>Phone:</label>
                          <input
                            type="text"
                            value={editedLead.phone}
                            onChange={(e) => handleEditChange('phone', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                      </div>
                      
                      {/* Row 2: Email + Inquiry Type */}
                      <div className="detail-row-pair">
                        <div className="detail-card">
                          <label>Email:</label>
                          <input
                            type="email"
                            value={editedLead.email}
                            onChange={(e) => handleEditChange('email', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                        <div className="detail-card">
                          <label>Inquiry Type:</label>
                          <select
                            value={editedLead.inquiryType}
                            onChange={(e) => handleEditChange('inquiryType', e.target.value)}
                            className="edit-select"
                          >
                            <option value="Residential Solar">Residential Solar</option>
                            <option value="Commercial Solar">Commercial Solar</option>
                            <option value="Industrial Solar">Industrial Solar</option>
                            <option value="Agricultural Solar">Agricultural Solar</option>
                            <option value="Institutional Solar">Institutional Solar</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Row 3: Status + Date */}
                      <div className="detail-row-pair">
                        <div className="detail-card">
                          <label>Status:</label>
                          <select
                            value={editedLead.status}
                            onChange={(e) => handleEditChange('status', e.target.value)}
                            className="edit-select"
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="converted">Converted</option>
                          </select>
                        </div>
                        <div className="detail-card">
                          <label>Date:</label>
                          <span>{new Date(editedLead.createdAt).toLocaleDateString('en-IN')}</span>
                        </div>
                      </div>
                      
                      {/* Row 4: Subject (single card) */}
                      <div className="detail-row-single">
                        <div className="detail-card full-width">
                          <label>Subject:</label>
                          <input
                            type="text"
                            value={editedLead.subject}
                            onChange={(e) => handleEditChange('subject', e.target.value)}
                            className="edit-input"
                          />
                        </div>
                      </div>
                      
                      {/* Row 5: Message (single card) */}
                      <div className="detail-row-single">
                        <div className="detail-card full-width message-card">
                          <label>Message:</label>
                          <textarea
                            value={editedLead.message}
                            onChange={(e) => handleEditChange('message', e.target.value)}
                            className="edit-textarea"
                            rows="4"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {isEditingLead && (
                <div className="modal-footer">
                  {/* Edit Mode Buttons Only */}
                  <>
                    <button className="modal-btn secondary" onClick={handleCancelEdit}>
                      Cancel
                    </button>
                    <button className="modal-btn primary" onClick={handleUpdateLead}>
                      <i className="fas fa-save"></i>
                      Apply Changes
                    </button>
                  </>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Settings Content */}
        {activeSection === 'settings' && (
          <div className="settings-content">
            <div className="settings-section">
              <h3>Account Settings</h3>
              <div className="settings-grid">
                <div className="setting-card">
                  <div className="setting-icon">
                    <i className="fas fa-lock"></i>
                  </div>
                  <div className="setting-info">
                    <h4>Change Password</h4>
                    <p>Update your login password for security</p>
                    <button className="setting-btn" onClick={openPasswordModal}>
                      <i className="fas fa-key"></i>
                      Change Password
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={cancelDeleteLead}>
            <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Lead</h3>
                <button className="close-btn" onClick={cancelDeleteLead}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-content">
                <div className="delete-confirmation">
                  <div className="delete-icon">
                    <i className="fas fa-trash"></i>
                  </div>
                  <p>Are you sure you want to delete this Lead?</p>
                  <p className="delete-warning">This action cannot be undone.</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn danger" onClick={confirmDeleteLead}>
                  Yes
                </button>
                <button className="modal-btn secondary" onClick={cancelDeleteLead}>
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Blog Delete Confirmation Modal */}
        {showBlogDeleteModal && (
          <div className="modal-overlay" onClick={cancelDeleteBlog}>
            <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Blog</h3>
                <button className="close-btn" onClick={cancelDeleteBlog}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-content">
                <div className="delete-confirmation">
                  <div className="delete-icon">
                    <i className="fas fa-trash"></i>
                  </div>
                  <p>Are you sure you want to delete this Blog?</p>
                  <p className="delete-warning">This action cannot be undone.</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn danger" onClick={confirmDeleteBlog}>
                  Yes
                </button>
                <button className="modal-btn secondary" onClick={cancelDeleteBlog}>
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Blog View Modal */}
        {showBlogViewModal && viewingBlog && (
          <div className="modal-overlay" onClick={closeBlogViewModal}>
            <div className="blog-view-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Blog Details</h3>
                <button className="close-btn" onClick={closeBlogViewModal}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-content">
                <div className="blog-view-content">
                  <div className="blog-detail-row">
                    <div className="detail-card">
                      <label>Title:</label>
                      <span>{viewingBlog.title}</span>
                    </div>
                  </div>
                  
                  <div className="blog-detail-row">
                    <div className="detail-card">
                      <label>Description:</label>
                      <span>{viewingBlog.description}</span>
                    </div>
                  </div>
                  
                  <div className="blog-detail-row">
                    <div className="detail-card">
                      <label>Publish Date:</label>
                      <span>{new Date(viewingBlog.publishDate).toLocaleDateString('en-IN')}</span>
                    </div>
                    <div className="detail-card">
                      <label>Status:</label>
                      <span className="status-badge published">Published</span>
                    </div>
                  </div>

                  {viewingBlog.image && (
                    <div className="blog-detail-row">
                      <div className="detail-card full-width">
                        <label>Featured Image:</label>
                        <div className="blog-image-preview">
                          <img src={viewingBlog.image} alt={viewingBlog.title} />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {viewingBlog.content && (
                    <div className="blog-detail-row">
                      <div className="detail-card full-width">
                        <label>Content:</label>
                        <div className="blog-content-preview">
                          {viewingBlog.content}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button className="modal-btn secondary" onClick={closeBlogViewModal}>
                  Close
                </button>
                <button className="modal-btn primary" onClick={() => {
                  closeBlogViewModal();
                  handleEdit(viewingBlog);
                }}>
                  <i className="fas fa-edit"></i>
                  Edit
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="modal-overlay">
            <div className="modal password-modal">
              <div className="modal-header">
                <h3>Change Password</h3>
                <button className="modal-close-elegant" onClick={closePasswordModal}>
                  <span className="close-line close-line-1"></span>
                  <span className="close-line close-line-2"></span>
                  <div className="close-ripple"></div>
                </button>
              </div>
              
              <form onSubmit={handlePasswordSubmit} className="password-form">
                <div className="form-group">
                  <label>Current Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      name="currentPassword"
                      value={passwordForm.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter current password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleCurrentPasswordVisibility}
                    >
                      <i className={`fas ${showCurrentPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      value={passwordForm.newPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter new password (min 6 characters)"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleNewPasswordVisibility}
                    >
                      <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={passwordForm.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleConfirmPasswordVisibility}
                    >
                      <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                </div>

                {passwordError && (
                  <div className="password-error">
                    <i className="fas fa-exclamation-triangle"></i>
                    {passwordError}
                  </div>
                )}

                {passwordSuccess && (
                  <div className="password-success">
                    <i className="fas fa-check-circle"></i>
                    {passwordSuccess}
                  </div>
                )}

                <div className="modal-footer">
                  <button type="button" className="modal-btn secondary" onClick={closePasswordModal}>
                    Cancel
                  </button>
                  <button type="submit" className="modal-btn primary">
                    <i className="fas fa-key"></i>
                    Update Password
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
